import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Check, Warning, ArrowRight } from '@phosphor-icons/react';
import { api } from '../../../context/AuthContext';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '../../ui/dialog';

const stageLabel = (stage) => (stage || '').replace(/_/g, ' ');

const StageButton = ({ item, onSelect }) => {
  const tone = item.is_current
    ? 'bg-primary text-white border-primary'
    : item.is_past
      ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:border-emerald-400'
      : 'bg-white text-foreground-muted border-black/[0.08] hover:border-primary/50 hover:text-slate-900';

  return (
    <button
      onClick={() => onSelect(item)}
      disabled={item.is_current}
      className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full border text-xs font-medium transition-colors disabled:cursor-default ${tone}`}
      data-testid={`workflow-stage-${item.status}`}
      title={item.is_current ? 'Current stage' : `Move to ${item.label}`}
    >
      {item.is_past && <Check size={12} weight="bold" />}
      {item.label}
      {item.is_recommended && !item.is_current && <ArrowRight size={11} weight="bold" className="text-primary" />}
    </button>
  );
};

/**
 * One-click project workflow: click the stage you want, confirm the implications.
 * Status, the project stage and every public/investor view stay in sync server-side.
 */
export const ProjectStatusWorkflow = ({ projectId, projectTitle, onChanged }) => {
  const [data, setData] = useState(null);
  const [target, setTarget] = useState(null);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get(`/admin/v2/projects/${projectId}/status-options`)
      .then((res) => setData(res.data))
      .catch(() => setData(null));
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const confirm = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/v2/projects/${projectId}/status`, {
        new_status: target.status,
        reason: reason.trim() || `Stage moved to ${target.label} by admin`,
        force: true,
      });
      toast.success(`Stage updated to ${target.label}`);
      setTarget(null);
      setReason('');
      load();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update stage');
    } finally {
      setSaving(false);
    }
  };

  if (!data) return null;

  const current = data.pipeline.find((p) => p.is_current) || data.other.find((p) => p.is_current);

  return (
    <div className="bg-white p-5 md:p-6 rounded-xl border border-black/[0.04]" data-testid="project-status-workflow">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="font-semibold">Project stage</h3>
        <p className="text-xs text-foreground-muted">
          Currently <span className="font-medium text-slate-900">{current?.label || data.current_status}</span>
          {' · '}project stage <span className="capitalize">{stageLabel(data.current_stage)}</span>
        </p>
      </div>
      <p className="text-sm text-foreground-muted mt-1.5">
        Click any stage to move the project there. We will tell you what changes before it is applied.
      </p>

      <div className="mt-5 flex flex-wrap items-center gap-2" data-testid="workflow-pipeline">
        {data.pipeline.map((item) => (
          <StageButton key={item.status} item={item} onSelect={setTarget} />
        ))}
      </div>

      <div className="mt-5 pt-4 border-t border-black/[0.05]">
        <p className="text-[11px] uppercase tracking-[0.14em] text-foreground-muted mb-2.5">Other outcomes</p>
        <div className="flex flex-wrap items-center gap-2">
          {data.other.map((item) => (
            <StageButton key={item.status} item={item} onSelect={setTarget} />
          ))}
        </div>
      </div>

      {(data.context?.subscription_count > 0 || data.context?.has_exit_results) && (
        <p className="mt-4 text-xs text-foreground-muted" data-testid="workflow-context">
          {data.context.subscription_count > 0 && (
            <>{data.context.subscription_count} subscription(s) · €{Math.round(data.context.invested_amount).toLocaleString()} committed. </>
          )}
          {data.context.has_exit_results && <>Exit results recorded.</>}
        </p>
      )}

      <Dialog open={!!target} onOpenChange={(open) => { if (!open) { setTarget(null); setReason(''); } }}>
        <DialogContent className="sm:max-w-[480px]" data-testid="workflow-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Move to {target?.label}?</DialogTitle>
            <DialogDescription>
              {projectTitle} moves from {current?.label} to {target?.label}. The project stage becomes{' '}
              <span className="capitalize">{stageLabel(target?.stage)}</span>.
            </DialogDescription>
          </DialogHeader>

          {target?.implications?.length > 0 && (
            <ul className="space-y-2.5" data-testid="workflow-implications">
              {target.implications.map((note) => (
                <li key={note} className="flex gap-2.5 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5">
                  <Warning size={16} weight="fill" className="text-amber-500 mt-0.5 flex-shrink-0" />
                  {note}
                </li>
              ))}
            </ul>
          )}

          <div className="space-y-1.5">
            <Label className="text-xs">Reason (optional, stored in the audit trail)</Label>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={`Stage moved to ${target?.label}`}
              data-testid="workflow-reason-input"
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setTarget(null); setReason(''); }}>Cancel</Button>
            <Button onClick={confirm} disabled={saving} data-testid="workflow-confirm-btn">
              {saving ? 'Updating…' : `Move to ${target?.label}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProjectStatusWorkflow;
